import { useEffect, useRef } from 'react';

// 터치 지점에서 위로 올라가며 해당 방향으로 더 스크롤 가능한 조상 요소 탐색 //
function findScrollableAncestor(target, deltaY, deltaX, isMovingVertically) {
  let el = target;

  while (el && el.nodeType === 1 && el !== document.body) {
    const style = window.getComputedStyle(el);

    if (isMovingVertically) {
      const overflowY = style.overflowY;
      const isScrollableV = (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight;

      if (isScrollableV) {
        const isAtTop = el.scrollTop <= 0;
        const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

        if (!(isAtTop && deltaY > 0) && !(isAtBottom && deltaY < 0)) return el;
      }
    }
    else {
      const overflowX = style.overflowX;
      const isScrollableH = (overflowX === 'auto' || overflowX === 'scroll') && el.scrollWidth > el.clientWidth;

      if (isScrollableH) {
        const isAtLeft = el.scrollLeft <= 0;
        const isAtRight = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;

        if (!(isAtLeft && deltaX > 0) && !(isAtRight && deltaX < 0)) return el;
      }
    }

    el = el.parentElement;
  }

  return null;
}

// 모달 스크롤 제어 훅 (iOS Safari/Chrome 대응 포함, 수평/수직 스크롤 지원) //
export default function useModalScroll(options = {}) {
  const {
    includeViewportSync = true,
    viewportTopCssVar = '--modal-viewport-top',
    viewportHeightCssVar = '--modal-viewport-height',
    viewportTopSource = 'scrollY',
    viewportLeftCssVar = '--modal-viewport-left',
    viewportWidthCssVar = '--modal-viewport-width',
    viewportLeftSource = 'scrollX',
    keyboardAware = false
  } = options;

  // 소프트 키보드가 떴다고 판단하는 최소 높이 차이(px). 노트북 등 키보드 없는 환경은 0에 가까워 발동하지 않음 //
  const KEYBOARD_OPEN_THRESHOLD = 120;

  const topLevelRef = useRef();
  const touchStartY = useRef(0);
  const touchStartX = useRef(0);

  useEffect(() => {
    const topLevelNode = topLevelRef.current;
    document.body.style.overflow = 'hidden';

    // iOS 뷰포트 바운스 방지: 모달 스크롤 경계 도달 시 touchmove 차단 //
    function handleTouchStart(e) {
      touchStartY.current = e.touches[0].clientY;
      touchStartX.current = e.touches[0].clientX;
    }

    function handleTouchMove(e) {
      const el = topLevelRef.current;
      if (!el) return;

      const currentY = e.touches[0].clientY;
      const currentX = e.touches[0].clientX;
      const deltaY = currentY - touchStartY.current;
      const deltaX = currentX - touchStartX.current;

      // 주된 이동 방향 판단 //
      const isMovingVertically = Math.abs(deltaY) >= Math.abs(deltaX);

      // 터치 지점 기준으로 해당 방향으로 더 스크롤 가능한 요소(드롭다운 등 중첩 영역 포함) 탐색 //
      const scrollableTarget = findScrollableAncestor(e.target, deltaY, deltaX, isMovingVertically);

      // 스크롤 소비 가능한 요소가 있으면 브라우저 기본 스크롤 허용 //
      if (scrollableTarget) return;

      // 소비할 요소가 없으면 바운스/오버스크롤 차단 //
      e.preventDefault();
    }

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });

    // 모바일 브라우저 주소창 변동 시 viewport top/height CSS 변수 동기화 //
    function syncViewport() {
      if (!includeViewportSync) return;
      if (!topLevelRef.current) return;

      const vv = window.visualViewport;

      // ----- 세로(top/height) -----
      let viewportTop = 0;

      if (viewportTopSource === 'offsetTop') {
        viewportTop = vv ? Math.max(0, Math.round(vv.offsetTop)) : 0;
      }
      else if (viewportTopSource === 'innerMinusVV') {
        viewportTop = vv ? Math.max(0, Math.round(window.innerHeight - vv.height)) : 0;
      }
      else {
        viewportTop = Math.max(0, Math.round(window.scrollY || window.pageYOffset || 0));
      }

      const viewportHeight = Math.max(0, Math.round(window.innerHeight));

      // ----- 가로(left/width) -----
      let viewportLeft = 0;

      if (viewportLeftSource === 'offsetLeft') {
        viewportLeft = vv ? Math.round(vv.offsetLeft || 0) : 0;
      }
      else {
        viewportLeft = Math.round(window.scrollX || window.pageXOffset || 0);
      }

      const viewportWidth = Math.max(
        0,
        Math.round((vv && vv.width) ? vv.width : window.innerWidth)
      );

      const el = topLevelRef.current;
      
      el.style.setProperty(viewportTopCssVar, `${viewportTop}px`);
      el.style.setProperty(viewportHeightCssVar, `${viewportHeight}px`);

      el.style.setProperty(viewportLeftCssVar, `${viewportLeft}px`);
      el.style.setProperty(viewportWidthCssVar, `${viewportWidth}px`);
    }

    // 소프트 키보드가 떴을 때만 모달 하단에 키보드 높이만큼 여백을 주고 포커스 입력창을 위로 스크롤 //
    // (모달 크기/위치는 그대로 두므로 노트북·아이패드 레이아웃이 깨지지 않음) //
    function adjustForKeyboard() {
      if (!keyboardAware) return;

      const container = topLevelRef.current;
      const vv = window.visualViewport;

      if (!container || !vv) return;

      const keyboardHeight = Math.round(window.innerHeight - vv.height);
      const isKeyboardOpen = keyboardHeight > KEYBOARD_OPEN_THRESHOLD;

      if (!isKeyboardOpen) {
        // 키보드가 닫히면 추가 여백 제거 //
        container.style.removeProperty('padding-bottom');
        return;
      }

      // 입력창이 키보드 위로 스크롤될 수 있도록 하단 여백 확보 //
      container.style.paddingBottom = `${keyboardHeight}px`;

      const active = document.activeElement;

      if (!active) return;

      const tagName = active.tagName;
      const isEditable = tagName === 'INPUT' || tagName === 'TEXTAREA' || active.isContentEditable;

      if (!isEditable) return;
      if (!container.contains(active)) return;

      active.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    // 뷰포트 크기 변화(키보드 등) 시 동기화 후 키보드 대응 //
    function handleViewportResize() {
      syncViewport();
      adjustForKeyboard();
    }

    syncViewport();

    if (includeViewportSync) {
      window.addEventListener('resize', handleViewportResize);
      window.addEventListener('orientationchange', handleViewportResize);

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleViewportResize);
        window.visualViewport.addEventListener('scroll', syncViewport);
      }
    }

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);

      if (includeViewportSync) {
        if (topLevelNode) {
          topLevelNode.style.removeProperty(viewportTopCssVar);
          topLevelNode.style.removeProperty(viewportHeightCssVar);
          topLevelNode.style.removeProperty(viewportLeftCssVar);
          topLevelNode.style.removeProperty(viewportWidthCssVar);
          topLevelNode.style.removeProperty('padding-bottom');
        }

        window.removeEventListener('resize', handleViewportResize);
        window.removeEventListener('orientationchange', handleViewportResize);

        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', handleViewportResize);
          window.visualViewport.removeEventListener('scroll', syncViewport);
        }
      }
    };
  }, [
    includeViewportSync,
    viewportHeightCssVar,
    viewportTopCssVar,
    viewportTopSource,
    viewportLeftCssVar,
    viewportWidthCssVar,
    viewportLeftSource,
    keyboardAware
  ]);

  return topLevelRef;
}
